import { useCallback, useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

import apiService from "../../../service/Api";
import { IFormProps, IRateCardProps } from "../types/rate-card";
import {
  AssignmentRateResolution,
  AssignmentRoleInfo,
  buildFixedRatePayload,
  createAssignmentRateForm,
  createEmptyAssignmentRateForm,
  fetchFixedRoleRate,
  getAssignmentRoleLabel,
  normalizeAssignmentRate,
} from "../utils/assignmentRates";

type PendingAssignmentCallback = (
  rate: AssignmentRateResolution,
) => Promise<void> | void;

interface EnsureRateOptions {
  effectiveDate?: string;
}

const validateRateForm = (formData: IFormProps) => {
  if (!formData.roleId) {
    toast.error("Please select the role for this rate card.");
    return false;
  }

  if (!formData.fixed_rate) {
    toast.error("Please provide the rate amount for this role.");
    return false;
  }

  if (!formData.currency) {
    toast.error("Please select the currency for this role.");
    return false;
  }

  if (!formData.effective_date) {
    toast.error("Please provide the effective date for this role rate.");
    return false;
  }

  return true;
};

export const useAssignmentRateGuard = () => {
  const [selectedRole, setSelectedRole] = useState<AssignmentRoleInfo | null>(
    null,
  );
  const [rateFormData, setRateFormData] = useState<IFormProps>(
    createEmptyAssignmentRateForm(),
  );
  const [rateFormLoading, setRateFormLoading] = useState(false);
  const [showMissingRateDialog, setShowMissingRateDialog] = useState(false);
  const [showCreateRateDialog, setShowCreateRateDialog] = useState(false);

  const pendingAssignmentRef = useRef<PendingAssignmentCallback | null>(null);
  const pendingResolutionRef = useRef<((value: boolean) => void) | null>(null);
  const pendingRejectionRef = useRef<((reason?: unknown) => void) | null>(null);

  const clearPendingRefs = useCallback(() => {
    pendingAssignmentRef.current = null;
    pendingResolutionRef.current = null;
    pendingRejectionRef.current = null;
  }, []);

  const clearRateGuardState = useCallback(() => {
    setSelectedRole(null);
    setRateFormData(createEmptyAssignmentRateForm());
    setRateFormLoading(false);
    setShowMissingRateDialog(false);
    setShowCreateRateDialog(false);
  }, []);

  const resetRateGuard = useCallback(() => {
    clearPendingRefs();
    clearRateGuardState();
  }, [clearPendingRefs, clearRateGuardState]);

  const ensureRateForRole = useCallback(
    async (
      role: AssignmentRoleInfo,
      onResolved: PendingAssignmentCallback,
      options?: EnsureRateOptions,
    ) => {
      const existingRate = await fetchFixedRoleRate(role.id);

      if (existingRate) {
        await onResolved(existingRate);
        return true;
      }

      return new Promise<boolean>((resolve, reject) => {
        pendingAssignmentRef.current = onResolved;
        pendingResolutionRef.current = resolve;
        pendingRejectionRef.current = reject;
        setSelectedRole(role);
        setRateFormData(
          createAssignmentRateForm(role, options?.effectiveDate),
        );
        setShowMissingRateDialog(true);
      });
    },
    [],
  );

  const handleMissingRateCancel = useCallback(() => {
    const resolvePending = pendingResolutionRef.current;

    resetRateGuard();
    resolvePending?.(false);
  }, [resetRateGuard]);

  const handleMissingRateAdd = useCallback(() => {
    setShowMissingRateDialog(false);
    setShowCreateRateDialog(true);
  }, []);

  const handleRateInputChange = useCallback((field: string, value: string) => {
    setRateFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleCreateRate = useCallback(async () => {
    if (!validateRateForm(rateFormData)) {
      return;
    }

    setRateFormLoading(true);

    try {
      const response = await apiService.post<IRateCardProps>(
        "/api/crews/role-ratings/",
        buildFixedRatePayload(rateFormData),
      );

      const resolvedRate = normalizeAssignmentRate(response.data);
      const pendingAssignment = pendingAssignmentRef.current;
      const resolvePending = pendingResolutionRef.current;
      const rejectPending = pendingRejectionRef.current;

      toast.success("Rate card created successfully.");
      resetRateGuard();

      if (pendingAssignment) {
        try {
          await pendingAssignment(resolvedRate);
          resolvePending?.(true);
        } catch (error) {
          rejectPending?.(error);
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.message ||
            "Failed to create a rate card. Please try again.",
        );
      } else {
        toast.error("Failed to create a rate card. Please try again.");
      }
    } finally {
      setRateFormLoading(false);
    }
  }, [rateFormData, resetRateGuard]);

  const missingRateDialogProps = useMemo(
    () => ({
      open: showMissingRateDialog,
      roleName: selectedRole?.name,
      onAddRate: handleMissingRateAdd,
      onCancel: handleMissingRateCancel,
    }),
    [
      handleMissingRateAdd,
      handleMissingRateCancel,
      selectedRole?.name,
      showMissingRateDialog,
    ],
  );

  const assignmentRateDialogProps = useMemo(
    () => ({
      open: showCreateRateDialog,
      formData: rateFormData,
      formLoading: rateFormLoading,
      roleLabel: selectedRole ? getAssignmentRoleLabel(selectedRole) : "",
      onClose: handleMissingRateCancel,
      onInputChange: handleRateInputChange,
      onSubmit: handleCreateRate,
    }),
    [
      handleCreateRate,
      handleMissingRateCancel,
      handleRateInputChange,
      rateFormData,
      rateFormLoading,
      selectedRole,
      showCreateRateDialog,
    ],
  );

  return {
    ensureRateForRole,
    missingRateDialogProps,
    assignmentRateDialogProps,
  };
};

export default useAssignmentRateGuard;
